<template>
    <div class="DdrDynamicForm">
        <DdrDynamicFormBanner
            v-if="nonDirtiedHighConfidenceFieldsCount > 0"
            :ai-fields-count="nonDirtiedHighConfidenceFieldsCount"
            :show-ai-fields="showAiFields"
            @update-ai-fields-visibility="setAiFieldsVisibility"
        />
        <component
            :is="getComponent(field.type)"
            v-for="field in fieldsToRender"
            v-show="!fieldsToHide[field.path]"
            :ref="String(field.path)"
            :key="field.path"
            class="DdrDynamicForm__field"
            :form-id="formId"
            :field-path="field.path"
            :rbac-disabled="rbacDisabled"
        />
    </div>
</template>

<style lang="scss">
    .DdrDynamicForm {
        padding: 16px;

        .DdrDynamicFormBanner {
            margin-bottom: 16px;
        }

        &__field + &__field {
            margin-top: 16px;
        }

        .DefaultLayout__inputContainer > .CheckboxInput {
            /* this should ideally be redone with a wrapper component to simplify all the shared styling and logic
            otherwise we will need to repeat the same pattern we tried to get way which we need to add
            DdrXxxInput__notFound for each input component in the scss.
            the current inputContainer > .CheckboxInput is not realiable and feels like a hack. */
            width: 90px;
        }
    }
</style>

<script lang="ts">
    import { defineAsyncComponent, defineComponent, PropType } from 'vue';

    import { DocumentDataReviewField } from '@evidentid/tprm-portal-lib/models/document-data-review';

    import { AM_BEST_FIELDS, AM_BEST_FIELD_FIRST } from '@/modules/document-data-review/constants/am-best';
    import { useDocumentDataReviewStore } from '@/modules/document-data-review/store';
    import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
    import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
    import { FieldType } from '@/modules/dynamic-form/models/FieldType.model';
    import { TypeDefinition, TypeDefinitionList } from '@/modules/dynamic-form/models/TypeDefinition.model';
    import { useDynamicFormStore } from '@/modules/dynamic-form/store';
    import { getControlComponents, ComponentResolver } from '@/modules/dynamic-form/utils/get-control-components/getControlComponents';
    import { toFieldPath } from '@/modules/dynamic-form/utils/to-field-path/toFieldPath';

    import DdrDynamicFormBanner from './sub-components/DdrDynamicFormBanner.vue';

    const componentsMap: Partial<Record<FieldType, ComponentResolver>> = {
        String: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-text-input/DdrTextInput.vue')),
        Boolean: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-bool-input/DdrBoolInput.vue')),
        Date: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-date-input/DdrDateInput.vue')),
        Enum: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-enum-input/DdrEnumInput.vue')),
        Int: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-int-input/DdrIntInput.vue')),
        StringBoolean: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-string-boolean-input/DdrStringBooleanInput.vue')),
        Address: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-address-input/DdrAddressInput.vue')),
        List: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-list-input/DdrListInput.vue')),
        AmBest: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-am-best-input/DdrAmBestInput.vue')),
        ScheduledOrBlanket: defineAsyncComponent(() => import('@/modules/document-data-review/components/input-components/ddr-scheduled-or-blanket-input/DdrScheduledOrBlanketInput.vue')),
    };

    const AM_BEST_FIELD_META: FieldMeta = {
        label: 'AM Best ',
        path: toFieldPath('amBest'),
        type: {
            $type: 'AmBest',
        },
        required: true,
        defaultValue: undefined,
    };

    const specialComponentsMap: Record<string, ComponentResolver> = {};

    export default defineComponent({
        name: 'DdrDynamicForm',
        components: {
            DdrDynamicFormBanner,
        },
        props: {
            formId: {
                type: String as PropType<string>,
                default: '',
                required: true,
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        data() {
            return {
                showAiFields: false,
                scrollContainer: null as HTMLElement | null,
                highlightedField: '',
                highlightTimeout: null as any,
            };
        },
        computed: {
            fieldsToRender(): FieldMeta[] {
                let fieldsMeta: FieldMeta[] =
                    Object.values(useDynamicFormStore().forms[this.formId]?.fieldsMeta || {}) || [];

                const indexOfFirstAmBestField = fieldsMeta.findIndex((field) => field.path === AM_BEST_FIELD_FIRST);
                fieldsMeta[indexOfFirstAmBestField] = AM_BEST_FIELD_META;
                fieldsMeta = fieldsMeta.filter((field) => !AM_BEST_FIELDS.includes(field.path));
                return fieldsMeta;
            },
            dirtiedFields(): Record<string, boolean> {
                return useDynamicFormStore().forms[this.formId]?.dirtyFields || {};
            },
            fieldsToHide(): Record<string, boolean> {
                return useDocumentDataReviewStore().forms[this.formId]?.hiddenFields || {};
            },
            ddrFields(): Record<FieldPath, DocumentDataReviewField | null> {
                return useDocumentDataReviewStore().forms[this.formId]?.fields || {};
            },
            highConfidenceAiFieldsPaths(): FieldPath[] {
                return Object.keys(this.ddrFields)
                    .filter((path) => this.isHighConfidenceAiField(path as FieldPath)) as FieldPath[];
            },
            nonDirtiedHighConfidenceFieldsCount(): number {
                return this.highConfidenceAiFieldsPaths.filter((path) => !this.dirtiedFields[path as FieldPath]).length;
            },
        },
        mounted() {
            this.scrollContainer = this.$el.closest('.PageWrapper');
        },
        created() {
            this.setAiFieldsVisibility(false);
        },
        beforeUnmount() {
            if (this.highlightTimeout) {
                clearTimeout(this.highlightTimeout);
            }
        },
        methods: {
            getComponent(type: TypeDefinition) {
                if (type.$type === 'List' && (type as TypeDefinitionList).elementType.$type === 'Collateral') {
                    return specialComponentsMap.CollateralList;
                }
                return getControlComponents(type.$type as FieldType, componentsMap);
            },
            setAiFieldsVisibility(showAiFields: boolean) {
                this.showAiFields = showAiFields;
                useDocumentDataReviewStore().setHiddenFields({
                    formId: this.formId,
                    hiddenFields: this.highConfidenceAiFieldsPaths.reduce(
                        (acc, curr) => {
                            acc[curr as FieldPath] = this.dirtiedFields[curr as FieldPath]
                                ? false
                                : !showAiFields;
                            return acc;
                        }, {} as Record<FieldPath, boolean>,
                    ),
                });
            },
            isHighConfidenceAiField(path: FieldPath): boolean {
                const field = this.ddrFields[path as any];
                const confidenceZone = field?.autoExtractionInfo?.zone?.toLowerCase();
                return confidenceZone === 'green';
            },
        },
    });
</script>
